import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { localize } from 'common/utils';

import { VenueSelectItem } from './types';

const normalizeString = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD') // split a letter with diacritics into the base letter and the diacritics
    .replace(/[\u0300-\u036f]/g, ''); // remove diacritics

const hasSearchedText = (value: string, searchValue: string) =>
  value.includes(searchValue) || normalizeString(value).includes(normalizeString(searchValue));

const getSearchFilter = <T>({
  getStringsForSearch,
  searchValue,
}: {
  getStringsForSearch: (venue: VenueSelectItem<T>) => string[];
  searchValue: string;
}) => {
  return (venue: VenueSelectItem<T>) =>
    !searchValue || getStringsForSearch(venue).some(value => hasSearchedText(value, searchValue));
};

export const useSearchVenues = <T>(venues: VenueSelectItem<T>[]) => {
  const { locale } = useIntl();
  const [searchValue, setSearchValue] = useState('');

  const filterVenuesBySearchKeyword = getSearchFilter({
    getStringsForSearch: (venue: VenueSelectItem<T>) => [
      localize(venue.name, locale),
      localize(venue.name, 'en'),
      venue.address,
      venue.city,
      venue.post_code,
    ],
    searchValue,
  });
  const filteredVenues = useMemo(
    () => venues.filter(filterVenuesBySearchKeyword),
    [venues, filterVenuesBySearchKeyword],
  );
  return { searchValue, setSearchValue, venues: filteredVenues };
};
